import { z } from 'zod';
import { tdsDeductionSchema } from './withholding';
import { amountSchema, dateOnlySchema, exchangeRateSchema } from './common';
import { createExpenseSchema } from './expenses';
import { createInvoiceSchema, updatePaymentSchema } from './invoices';
import { updateBankAccountSchema } from './banking';
import { updateOrgSchema } from './orgs';
import { isValidGstinChecksum } from './org-settings';
import { GST_TREATMENTS } from './customers';
import { createItemSchema } from './items';
import { createCustomerSchema } from './customers';
/**
 * The Inbox: proposals a model made, waiting for a person.
 *
 * ── WHY THE BODY IS A LIST OF REFS AND NOT A BATCH ID ──
 * A batch is a grouping for the screen, never a unit of work. The person
 * unticks the two rows Claude read wrongly and confirms the other 140, so what
 * the server receives is the rows they left ticked - each named by its type
 * and id, because the three record types live in three tables and an id alone
 * says nothing about which. The server confirms them one at a time, each in
 * its own transaction, and reports per row.
 *
 * ── WHY 500 ──
 * More than a batch tool can propose in one call (200), so one screenful of
 * "Confirm all" always fits, and small enough that a single request cannot
 * hold a transaction per row open for minutes.
 */
/**
 * Five kinds: three new drafts, two more since 0017 (a draft item, a draft
 * customer) and a CHANGE to a record that exists.
 */
/**
 * `proforma` (R-06): a quote the agent drafted. It was the one thing an agent
 * made that the Inbox did not show - the agent said "created" and the person
 * could not find it. Like a draft invoice it is sent, not confirmed: the row
 * opens the quote, and Discard throws the draft away.
 */
export const PROPOSAL_TYPES = ['expense', 'deposit', 'invoice', 'proforma', 'item', 'customer', 'vendor', 'change'];
export const INBOX_ACTION_MAX = 500;
export const proposalRefSchema = z
    .object({
    type: z.enum(PROPOSAL_TYPES),
    id: z.string().trim().min(1).max(36),
})
    .strict();
export const inboxActionSchema = z
    .object({
    items: z.array(proposalRefSchema).min(1, 'Pick at least one row').max(INBOX_ACTION_MAX),
})
    .strict();
/**
 * A model taking back what it proposed (R-06). Not `discard`: discard is the
 * person's, from the Inbox, over anything waiting there. Withdraw reaches only
 * a row a MODEL made - its first audit entry says so - and says why, on the
 * trail. "Reject the 2 Oct Rs 1,169 pair" was the one Part A step a person
 * still had to do by hand, for rows the agent itself had drafted.
 */
export const withdrawProposalsSchema = z
    .object({
    items: z.array(proposalRefSchema).min(1, 'Name at least one row').max(50),
    reason: z.string().trim().min(3, 'Say why it is being withdrawn').max(500),
})
    .strict();
/**
 * Editing a proposed deposit before confirming it.
 *
 * The four things a model reads off a row and can read wrongly. Only a DRAFT
 * takes this - a confirmed deposit is in a balance and is edited nowhere, by
 * design - and `.strict()` so a `status` smuggled in is a 400, exactly as it
 * is on `updateExpenseSchema`.
 */
/**
 * What KIND of money a deposit is, when it is not a payment of an invoice.
 *
 * Income is the ordinary case. Capital is money a partner or owner put INTO
 * the business - the LLP's contribution, a proprietor topping up the account -
 * and it is not income in any sense a return or a P&L means: it lands on the
 * bank balance and in no income figure. A deposit used to have only the first
 * answer, so an opening contribution went in under "Other income" and the
 * year's income was overstated by it.
 *
 * An enum rather than a flag because there is a third answer coming: a refund
 * of a cost (requests.md R-04) is money in that is neither income nor capital,
 * and it slots in here as one more member.
 */
export const DEPOSIT_KINDS = ['income', 'capital'];
/** What a person calls each kind, for the label on a row and the choice in a sheet. */
export const DEPOSIT_KIND_LABELS = {
    income: 'Income',
    capital: 'Capital introduced',
};
/**
 * Capital is not income, so an income category on it is a contradiction - the
 * row would claim to be both. Refused in the schema so an API caller, the MCP
 * and the Inbox sheet get the same sentence.
 */
const capitalHasNoCategory = (v, ctx) => {
    if (v.kind === 'capital' && v.incomeCategoryId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['incomeCategoryId'],
            message: 'Capital introduced is not income, so it takes no income category. Leave the category empty.',
        });
    }
};
export const updateDraftDepositSchema = z
    .object({
    date: dateOnlySchema.optional(),
    amount: amountSchema.optional(),
    description: z.string().trim().max(300).nullable().optional(),
    incomeCategoryId: z.string().trim().max(36).nullable().optional(),
    /**
     * Which account it lands in. Claude reads "Hdfc personal" off a
     * statement and the person filing it knows it was the current account.
     * Never nullable - a deposit is always INTO somewhere - and only while
     * the row is a draft, like everything else here.
     */
    accountId: z.string().trim().min(1, 'Pick an account').max(36).optional(),
    /** Income or capital. Switching to capital clears the income category. */
    kind: z.enum(DEPOSIT_KINDS).optional(),
})
    .strict()
    .superRefine(capitalHasNoCategory);
// ── Suggestions ─────────────────────────────────────────────────────────────
/**
 * `GET /inbox/suggestions?type=expense&q=UPI - Mahendiran A`
 *
 * What this book filed the same payee under before. The person filing 25
 * statement rows one by one is the reader: `q` is the description as it
 * stands in the edit sheet, and the server works the payee out of it. Only
 * the two record types a person edits in the Inbox - an invoice has a page.
 * `.strict()` so a mistyped `type` is a 400 rather than an empty answer that
 * looks like "never seen this payee".
 */
export const SUGGESTION_TYPES = ['expense', 'deposit'];
export const inboxSuggestionsQuerySchema = z
    .object({
    type: z.enum(SUGGESTION_TYPES),
    q: z.string().trim().max(300).default(''),
})
    .strict();
/**
 * Proposing through the Inbox's own API - the path a CSV import, a script,
 * or the e2e suite takes; the MCP tools take the same service methods.
 *
 * Each carries the SAME body its record type's create takes, wrapped with an
 * optional batch, so there is one schema for "what an expense is" and this
 * file adds only "which batch". A proposal made here is checked by the same
 * permission as creating the record outright: a person who may not record an
 * expense may not propose one either.
 */
const batchIdSchema = z.string().trim().min(1).max(36).optional();
/**
 * The model's words about a proposal, kept beside it.
 *
 * Shown in the Inbox under the row (or the batch title) and never written
 * into the record it becomes: a note is context for the person deciding, not
 * a field of the expense. Optional everywhere, and never a reason to refuse
 * a row - `proposalNoteOf` trims it to the column.
 */
const proposalNoteSchema = z.string().trim().max(500).optional();
export const createProposalBatchSchema = z
    .object({
    title: z.string().trim().min(1, 'Say what this batch is').max(200),
    note: z.string().trim().max(1000).optional(),
})
    .strict();
export const draftDepositSchema = z
    .object({
    accountId: z.string().trim().min(1).max(36),
    date: dateOnlySchema,
    amount: amountSchema,
    description: z.string().trim().max(300).optional(),
    incomeCategoryId: z.string().trim().max(36).optional(),
    /** The counterparty as written - a payer name; not a customer id. */
    reference: z.string().trim().max(120).optional(),
    /** Income (the default) or capital a partner or owner put in. */
    kind: z.enum(DEPOSIT_KINDS).optional(),
    batchId: batchIdSchema,
    proposalNote: proposalNoteSchema,
})
    .strict()
    .superRefine(capitalHasNoCategory);
export const draftExpenseProposalSchema = z
    .object({ batchId: batchIdSchema, expense: createExpenseSchema, proposalNote: proposalNoteSchema })
    .strict();
export const draftInvoiceProposalSchema = z
    .object({ batchId: batchIdSchema, invoice: createInvoiceSchema, proposalNote: proposalNoteSchema })
    .strict();
// ── Across books ────────────────────────────────────────────────────────────
/**
 * `GET /inbox` with no `x-org-id`, or with `?scope=all`, lists every book the
 * person is an active member of. Anything else in the query is a 400 - a
 * typo in `scope` must not quietly narrow the list to one book.
 */
export const inboxListQuerySchema = z
    .object({
    scope: z.enum(['all']).optional(),
})
    .strict();
/**
 * Move proposals to another book - Claude drafted the electricity bill in
 * the company's book and it was the flat's.
 *
 * Under the SOURCE book's `x-org-id`, naming the target. Drafts only, and
 * only expenses and deposits: an invoice belongs to its customer's book and
 * is drafted there. The same per-row outcome as confirm, because a row that
 * cannot land in the target (no category of that name there) must not stop
 * the rows that can.
 */
export const moveProposalsSchema = z
    .object({
    items: z.array(proposalRefSchema).min(1, 'Pick at least one row').max(INBOX_ACTION_MAX),
    toOrgId: z.string().trim().min(1, 'Say which book to move it to').max(36),
})
    .strict();
// ── Draft items, draft customers, and changes to records that exist ────────
/**
 * What an agent may propose about a deposit that is already recorded: who
 * paid and for what, and the income category if it was missing. Nothing
 * here moves a figure - the amount and the date are not on this schema on
 * purpose - which is why it may be proposed at all.
 */
export const depositChangeSchema = z
    .object({
    partyId: z.string().trim().max(36).nullable().optional(),
    itemId: z.string().trim().max(36).nullable().optional(),
    incomeCategoryId: z.string().trim().max(36).nullable().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Propose at least one change' });
/**
 * The same for an expense: vendor and category - and, since an agent
 * reconciling a statement against the books found three kinds of mistake it
 * could only report to the developers, the date, the label, and deletion.
 *
 * The AMOUNT is still not here. A wrong amount on a recorded expense is a
 * different bank line, not a typo on this one, and moving a figure the person
 * typed is the one thing a proposal about it never does.
 */
export const expenseChangeSchema = z
    .object({
    vendorId: z.string().trim().max(36).nullable().optional(),
    categoryId: z.string().trim().max(36).optional(),
    /** The bill number as printed - what a matched receipt adds to a statement row. */
    referenceNumber: z.string().trim().max(120).nullable().optional(),
    /** The GST rate the bill shows, when the statement row had none. */
    taxId: z.string().trim().max(36).nullable().optional(),
    /** The date the bank shows, when the record has it a day out (POS 265602: booked 28 Apr, debited 29 Apr). */
    date: dateOnlySchema.optional(),
    /** What the row says it was - "DPO CGST", not the "DPO SGST" it was booked as. */
    notes: z.string().trim().max(2000).nullable().optional(),
    /**
     * Delete it: a duplicate (a salary booked on 1 Apr that the bank paid on
     * 30 Mar), or a card check the vendor refunded. Alone - a deletion
     * alongside an edit is two proposals pretending to be one.
     */
    delete: z.literal(true).optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Propose at least one change' })
    .refine((v) => !v.delete || Object.keys(v).length === 1, {
    message: 'A deletion is proposed on its own, not alongside other changes',
});
/**
 * Settle an invoice with a deposit that already arrived - the migration
 * case, where the money was recorded from a statement before the invoice
 * existed in Fint. Confirming records the payment and re-links the existing
 * deposit; it moves a receivable, which is why it is a proposal.
 */
export const settlementChangeSchema = z
    .object({
    invoiceId: z.string().trim().min(1).max(36),
    depositId: z.string().trim().min(1).max(36),
    /** Defaults to the smaller of the deposit plus what was withheld, and the invoice balance. */
    amount: amountSchema.optional(),
    /**
     * What the customer withheld before paying, so the invoice settles in
     * full on less cash. The ordinary case for an Indian B2B service: 2% or
     * 10% TDS off the taxable value, and the bank's charge on the way in.
     * Both go straight to `PaymentsService.record`, which already does the
     * arithmetic - a deduction is not credit, and has to be covered by what
     * is applied. A migration is where this shows up first: the deposit is
     * short of the invoice by exactly the TDS, and without these the last
     * few hundred rupees stay "Overdue" for ever.
     */
    tds: tdsDeductionSchema.optional(),
    bankCharges: amountSchema.optional(),
    /**
     * When the invoice is in one currency and the deposit in the book's
     * base (a £ invoice paid into the HDFC ₹ account): what the customer
     * paid in the INVOICE's currency. The deposit is the base cash and the
     * rate is derived from the two. Either this or `exchangeRate` is
     * required in that case; ignored when the two currencies agree.
     */
    received: amountSchema.optional(),
    exchangeRate: exchangeRateSchema.optional(),
})
    .strict();
/**
 * Money back on a recorded charge (0030) - the target is the CHARGE. Confirming
 * goes through `ExpensesService.refund`, which writes the negative row in the
 * charge's category, and adopts `bankTransactionId` as its cash side when the
 * credit is already in the account - including one that is still a DRAFT in
 * the Inbox, which is how a refund on a statement arrives: proposed as a
 * deposit before anybody knew it was a refund.
 */
export const refundChangeSchema = z
    .object({
    date: dateOnlySchema,
    amount: amountSchema,
    bankTransactionId: z.string().trim().max(36).optional(),
    referenceNumber: z.string().trim().max(120).optional(),
})
    .strict();
/**
 * A correction to a recorded payment: the account it landed in, or what the
 * bank actually credited. The SAME schema a person's correction goes through
 * (`updatePaymentSchema`), reason included - one shape for "correct a
 * payment", whoever proposes it.
 */
export const paymentChangeSchema = updatePaymentSchema;
/**
 * The details a reconciliation finds missing on an account: its last four
 * digits, IFSC, and the balance and date it opened with. Not its name,
 * currency or whether it is active - those are the person's decisions about
 * how the book is laid out, not facts a statement can settle.
 */
export const bankAccountChangeSchema = updateBankAccountSchema
    .pick({ accountNumberLast4: true, ifsc: true, openingBalance: true, openingBalanceDate: true })
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Propose at least one change' });
const gstinOk = (v) => !v.gstin || isValidGstinChecksum(v.gstin);
const GSTIN_REFUSAL = { path: ['gstin'], message: 'That GSTIN fails its checksum - copy it exactly as printed.' };
/**
 * The book's own details (R-06): what prints at the top of every invoice and
 * the GSTIN every return is filed under. Proposed - a new owner's details sent
 * over WhatsApp - and set only when a person with the settings permission
 * confirms, through the same two saves the Settings screen makes.
 */
export const bookChangeSchema = updateOrgSchema
    .pick({ legalName: true, email: true, phone: true, addressLine1: true, addressLine2: true, city: true, state: true, postalCode: true, country: true, logoFileId: true })
    .extend({ gstin: z.string().trim().toUpperCase().max(15).optional() })
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Propose at least one change' })
    .refine(gstinOk, GSTIN_REFUSAL);
/** A customer's or vendor's details that arrived late: a GSTIN, an email, a PAN. Never a figure. */
export const partyDetailsChangeSchema = z
    .object({
    displayName: z.string().trim().min(1).max(200).optional(),
    companyName: z.string().trim().max(200).optional(),
    email: z.string().trim().toLowerCase().email('Enter a valid email').optional().or(z.literal('')),
    phone: z.string().trim().max(40).optional(),
    gstin: z.string().trim().toUpperCase().max(15).optional().or(z.literal('')),
    pan: z.string().trim().toUpperCase().max(10).optional().or(z.literal('')),
    placeOfSupply: z.string().trim().toUpperCase().max(4).optional(),
    gstTreatment: z.enum(GST_TREATMENTS).optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Propose at least one change' })
    .refine(gstinOk, GSTIN_REFUSAL);
/**
 * What a recorded bank line a person entered IS, when it is not an expense or
 * a payment: a partner's drawing (money out), capital a partner put in, or
 * plain income (money in). Through `categorise`, whose direction check
 * refuses a drawing on a deposit and capital on a withdrawal.
 */
export const BANK_LINE_FILINGS = ['drawing', 'capital', 'income'];
export const bankLineChangeSchema = z.object({ fileAs: z.enum(BANK_LINE_FILINGS) }).strict();
export const CHANGE_TARGET_TYPES = [
    'bank_transaction',
    'expense',
    'settlement',
    'refund',
    'payment',
    'bank_account',
    'book',
    'customer_details',
    'vendor_details',
    'bank_line',
];
/**
 * The schema a change is read through, by what it targets. The ONLY way a
 * `changes_json` column is read: never applied raw, on the way in or the way
 * out, so a column written by an older release with a field the target's
 * update no longer takes is a refusal, not a silent write.
 *
 * A settlement's target is the INVOICE (`target_id` = the invoice id), and
 * its changes name the deposit - so the Inbox lists it under the document
 * it settles, and a second settlement of the same invoice is a second row
 * about the same target, which is what a person expects to see.
 */
export function changeSchemaFor(targetType) {
    // Every type named - no fall-through. An unknown type falling through to
    // the expense schema is how a new target would inherit an expense's rules
    // without anybody deciding it should.
    switch (targetType) {
        case 'bank_transaction':
            return depositChangeSchema;
        case 'settlement':
            return settlementChangeSchema;
        case 'expense':
            return expenseChangeSchema;
        case 'refund':
            return refundChangeSchema;
        case 'payment':
            return paymentChangeSchema;
        case 'bank_account':
            return bankAccountChangeSchema;
        case 'book':
            return bookChangeSchema;
        case 'customer_details':
        case 'vendor_details':
            return partyDetailsChangeSchema;
        case 'bank_line':
            return bankLineChangeSchema;
    }
}
/**
 * A draft item and a draft customer, through the Inbox's own API.
 *
 * The SAME body the record type's create takes - one schema for "what an
 * item is" - wrapped with the batch and the note every proposal carries. The
 * service forces `status = 'draft'` whatever arrived; there is no field on
 * either create schema that says otherwise, and none is added here.
 */
export const draftItemProposalSchema = z
    .object({ batchId: batchIdSchema, item: createItemSchema, proposalNote: proposalNoteSchema })
    .strict();
export const draftCustomerProposalSchema = z
    .object({ batchId: batchIdSchema, customer: createCustomerSchema, proposalNote: proposalNoteSchema })
    .strict();
/**
 * A proposed CHANGE to a record that exists.
 *
 * Discriminated on the target type so `changes` is parsed through exactly
 * the shape that target's update accepts - a `partyId` on an expense is a
 * 400 here, not a column the apply path has to know to ignore. The target
 * is untouched until a person confirms in the Inbox.
 */
export const proposeChangeSchema = z.discriminatedUnion('targetType', [
    z
        .object({
        targetType: z.literal('bank_transaction'),
        targetId: z.string().trim().min(1, 'Say which deposit').max(36),
        changes: depositChangeSchema,
        batchId: batchIdSchema,
        proposalNote: proposalNoteSchema,
    })
        .strict(),
    z
        .object({
        targetType: z.literal('expense'),
        targetId: z.string().trim().min(1, 'Say which expense').max(36),
        changes: expenseChangeSchema,
        batchId: batchIdSchema,
        proposalNote: proposalNoteSchema,
    })
        .strict(),
    /**
     * A settlement (0018): `targetId` is the invoice, `changes.invoiceId` the
     * same id said again so the column reads on its own, and `depositId` the
     * counted deposit that already arrived. The service refuses the two ids
     * disagreeing.
     */
    z
        .object({
        targetType: z.literal('settlement'),
        targetId: z.string().trim().min(1, 'Say which invoice').max(36),
        changes: settlementChangeSchema,
        batchId: batchIdSchema,
        proposalNote: proposalNoteSchema,
    })
        .strict(),
    z
        .object({
        targetType: z.literal('refund'),
        targetId: z.string().trim().min(1, 'Say which charge was refunded').max(36),
        changes: refundChangeSchema,
        batchId: batchIdSchema,
        proposalNote: proposalNoteSchema,
    })
        .strict(),
    z
        .object({
        targetType: z.literal('payment'),
        targetId: z.string().trim().min(1, 'Say which payment').max(36),
        changes: paymentChangeSchema,
        batchId: batchIdSchema,
        proposalNote: proposalNoteSchema,
    })
        .strict(),
    z
        .object({
        targetType: z.literal('bank_account'),
        targetId: z.string().trim().min(1, 'Say which account').max(36),
        changes: bankAccountChangeSchema,
        batchId: batchIdSchema,
        proposalNote: proposalNoteSchema,
    })
        .strict(),
    z
        .object({
        targetType: z.literal('book'),
        targetId: z.string().trim().min(1, 'Say which book').max(36),
        changes: bookChangeSchema,
        batchId: batchIdSchema,
        proposalNote: proposalNoteSchema,
    })
        .strict(),
    z
        .object({
        targetType: z.literal('customer_details'),
        targetId: z.string().trim().min(1, 'Say which customer').max(36),
        changes: partyDetailsChangeSchema,
        batchId: batchIdSchema,
        proposalNote: proposalNoteSchema,
    })
        .strict(),
    z
        .object({
        targetType: z.literal('vendor_details'),
        targetId: z.string().trim().min(1, 'Say which vendor').max(36),
        changes: partyDetailsChangeSchema,
        batchId: batchIdSchema,
        proposalNote: proposalNoteSchema,
    })
        .strict(),
    z
        .object({
        targetType: z.literal('bank_line'),
        targetId: z.string().trim().min(1, 'Say which bank line').max(36),
        changes: bankLineChangeSchema,
        batchId: batchIdSchema,
        proposalNote: proposalNoteSchema,
    })
        .strict(),
]);
/**
 * Revise a DRAFT - something already proposed and not yet confirmed.
 *
 * Not a change proposal: a draft IS a proposal, in no figure, and correcting
 * it before anybody confirms is still proposing. The agent reconciling a
 * statement drafted two ₹32,450 rows as Travel, was told they were food at a
 * client conference, and could only say "change both in the Inbox before
 * confirming". Now it changes them. A RECORDED row is refused here - that is
 * what a correction proposal is for, and it waits for a person.
 */
export const REVISABLE_DRAFT_TYPES = ['expense', 'deposit'];
export const reviseDraftSchema = z
    .object({
    type: z.enum(REVISABLE_DRAFT_TYPES),
    id: z.string().trim().min(1).max(36),
    changes: z
        .object({
        categoryId: z.string().trim().max(36).optional(),
        date: dateOnlySchema.optional(),
        amount: amountSchema.optional(),
        notes: z.string().trim().max(2000).optional(),
        accountId: z.string().trim().max(36).optional(),
        /** A deposit only: income, or capital a partner put in. Capital carries no category. */
        kind: z.enum(DEPOSIT_KINDS).optional(),
    })
        .strict()
        .refine((v) => Object.keys(v).length > 0, { message: 'Revise at least one thing' }),
    proposalNote: proposalNoteSchema,
})
    .strict();
// ── Matching a bill to the row it belongs to ────────────────────────────────
/**
 * `GET /inbox/matches?kind=expense&date=2026-08-12&amount=2360&counterparty=Albi Maria`
 *
 * "Which recorded expense is this bill for?" - the migration case, where the
 * statement was imported first and the supplier's invoices arrive after. The
 * same question for a deposit: "which counted deposit paid this invoice?".
 * Recorded rows only, ranked, up to five, each with a sentence saying why;
 * the person (or the agent that asked) picks. Nothing here writes.
 *
 * `amount` is optional but exact when given: a bill for ₹2,360 is not the
 * ₹2,300 row two days earlier, and a matcher that said "close enough" would
 * attach a receipt to the wrong spend. `window` is days either side of `date`.
 */
export const MATCH_KINDS = ['expense', 'deposit'];
export const MATCH_WINDOW_DEFAULT_DAYS = 5;
export const MATCH_WINDOW_MAX_DAYS = 90;
export const MATCH_CANDIDATES_MAX = 5;
export const inboxMatchesQuerySchema = z
    .object({
    kind: z.enum(MATCH_KINDS),
    date: dateOnlySchema,
    amount: amountSchema.optional(),
    /** The name on the bill or the payer, as written. Matched by its words against the row. */
    counterparty: z.string().trim().max(200).optional(),
    /** The bill number or a statement reference. An exact match outranks everything but the amount. */
    reference: z.string().trim().max(120).optional(),
    window: z.coerce.number().int().min(0).max(MATCH_WINDOW_MAX_DAYS).default(MATCH_WINDOW_DEFAULT_DAYS),
})
    .strict();
/**
 * Fixing a pending change before confirming it: `PATCH /inbox/changes/:id`.
 *
 * Every key either change shape has, so one body serves both targets; the
 * service merges it into what the change proposed and re-parses the result
 * through the TARGET's schema, which is where a `vendorId` on a deposit is
 * refused. `null` proposes clearing the field; a key left out stays as it
 * was proposed. `.strict()`, so a `targetId` smuggled in is a 400.
 */
export const updateChangeSchema = z
    .object({
    partyId: z.string().trim().max(36).nullable().optional(),
    itemId: z.string().trim().max(36).nullable().optional(),
    incomeCategoryId: z.string().trim().max(36).nullable().optional(),
    vendorId: z.string().trim().max(36).nullable().optional(),
    categoryId: z.string().trim().max(36).optional(),
    /** The two a matched bill adds to an expense (0018). On a deposit either is a 400, from the target's schema. */
    referenceNumber: z.string().trim().max(120).nullable().optional(),
    taxId: z.string().trim().max(36).nullable().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Change at least one field' });
//# sourceMappingURL=proposals.js.map